import { Controller } from "@hotwired/stimulus";
import { ContextController } from "@/view-switch/context-controller";
import { Expandable, ExpandableDelegate, ExpandableSettings } from "./expandable";
import { ContainerExpansionStrategy } from "./container-expansion-strategy";
import { RowExpansionStrategy } from "./row-expansion-strategy";

export interface ExpandableContext {
    component: string;
    settings: ExpandableSettings;
    preloaded?: boolean;
}

type ExpansionStatus = "idle" | "loading" | "loaded";

export interface ExpandableApi {
    /** Returns expanded HTML content for this expandable item. */
    getContent(data: FormData, loadingElement: HTMLElement): Promise<string>;
}

export interface ExpansionStrategy {
    /** Hides the collapsible content */
    collapse(content: Expandable): void;
    /** Shows the collapsible content. Called after any necessary content has been loaded */
    expand(content: Expandable): void;
    /** Returns the element on which a loading spinner should be shown while loading content for this expandable item. */
    getLoadingElement(content: Expandable): HTMLElement;
    /** Inserts the loaded content HTML into to the page. Content is only loaded once. */
    applyLoadedContent(content: Expandable, html: string): void;
}

export abstract class ExpandableController extends Controller<HTMLElement> implements ExpandableDelegate {
    static values = { contentId: String };
    static outlets = ["context"];

    declare readonly contentIdValue: string;
    declare readonly hasContentIdValue: boolean;
    declare readonly contextOutlet: ContextController<ExpandableContext>;

    private expandable!: Expandable;
    private strategy!: ExpansionStrategy;
    private api!: ExpandableApi;
    private state: ExpansionStatus = "idle";
    private contentId: string;

    connect(): void {
        this.expandable = new Expandable(this.element, this.contextOutlet.contextData.settings);
        this.expandable.connect(this);
        this.strategy = this.createStrategy();
        this.api = this.createApi();
        this.contentId = this.hasContentIdValue ? this.contentIdValue : this.element.id;

        if (this.contextOutlet.contextData.preloaded) {
            this.state = "loaded";
        }
    }

    disconnect(): void {
        this.expandable.disconnect();
        this.state = "idle";
    }

    transitionToExpandedState(content: Expandable, expanded: boolean): void {
        if (!expanded) {
            this.strategy.collapse(content);
            return;
        }

        if (this.state === "loaded") {
            this.strategy.expand(content);
            return;
        }

        if (this.state === "loading") {
            return;
        }

        this.state = "loading";
        this.api.getContent(this.createRequestData(), this.strategy.getLoadingElement(content))
            .then(html => {
                this.strategy.applyLoadedContent(content, html);
                this.state = "loaded";
                this.strategy.expand(content);
            })
            .catch(() => {
                this.state = "idle";
            });
    }

    collapse(): void {
        this.strategy.collapse(this.expandable);
    }

    private createStrategy(): ExpansionStrategy {
        if (this.expandable.content) {
            return new ContainerExpansionStrategy();
        }

        return new RowExpansionStrategy(
            this.application,
            this.identifier,
        );
    }

    private createRequestData(): FormData {
        const form = new FormData();
        form.append("component", this.contextOutlet.contextData.component);
        form.append("contextId", this.contextOutlet.contextId);
        form.append("contentId", this.contentId);
        return form;
    }

    // Stimulus JS does not allow constructor injection, so use subclass to provide dependencies
    protected abstract createApi(): ExpandableApi;
}
